/**
 * Macros store — the PWA-side canonical library of macros.
 *
 * Lives in localStorage under MACROS_KEY. On bridge connect, the active
 * library is sent up via `bridge.saveMacros(library)` so the bridge can
 * execute them.
 *
 * Single global library for now — not tied to projects. Macros are
 * reusable across layouts/gigs by design (see CONTINUE.md M2-Q1
 * decision: macros live in their own document).
 *
 * Across devices: each `project-<id>` sync doc carries the macros its items
 * reference (`layout/projectSync.ts`, `layout/libraryMerge.ts`), newest
 * `modifiedAt` wins per macro. Before 2026-09-28 a macro item arrived on
 * another device pointing at a macro that device did not have.
 */

import { createSignal, type Accessor } from 'solid-js';
import {
  type Macro,
  type MacroLibrary,
  MACROS_SCHEMA_VERSION,
} from '@opbuttons/schema';
import { ulid } from '../utils/ulid.ts';
import { createHistory } from '../undo/history.ts';
import { mergeLibraryEntries, stampChanged } from '../layout/libraryMerge.ts';

const MACROS_KEY = 'opbuttons-macros-v1';

function emptyLibrary(): MacroLibrary {
  return { schemaVersion: MACROS_SCHEMA_VERSION, macros: [] };
}

/**
 * Seed library used on first run, so the user has something to tap before
 * they've built anything. Two stock macros — one Riser (sine on CC 70), one
 * Drop (exp ramp on CC 71). Removable like any other.
 */
function seedLibrary(): MacroLibrary {
  return {
    schemaVersion: MACROS_SCHEMA_VERSION,
    macros: [
      {
        id: ulid(),
        name: 'Riser',
        triggerLengthBeats: 16,
        lanes: [
          {
            id: ulid(),
            label: 'CC 70 - filter sweep',
            target: { bridge: 'pc', kind: 'cc', channel: 0, number: 70 },
            lengthBeats: 16,
            shape: { kind: 'sine', skew: 0.4 },
            center: 64,
            amplitude: 63,
          },
        ],
      },
      {
        id: ulid(),
        name: 'Drop',
        triggerLengthBeats: 4,
        lanes: [
          {
            id: ulid(),
            label: 'CC 71 - exp pull',
            target: { bridge: 'pc', kind: 'cc', channel: 0, number: 71 },
            lengthBeats: 4,
            shape: { kind: 'exp' },
            center: 64,
            amplitude: 63,
          },
        ],
      },
    ],
  };
}

function load(): MacroLibrary {
  try {
    const raw = localStorage.getItem(MACROS_KEY);
    if (!raw) return seedLibrary();
    const parsed = JSON.parse(raw) as MacroLibrary;
    if (parsed.schemaVersion !== MACROS_SCHEMA_VERSION) {
      // future migration hook
      console.warn(`macros: unknown schemaVersion ${parsed.schemaVersion}, starting empty`);
      return emptyLibrary();
    }
    return parsed;
  } catch (err) {
    console.warn('macros: load failed', err);
    return emptyLibrary();
  }
}

function persist(lib: MacroLibrary): void {
  try {
    localStorage.setItem(MACROS_KEY, JSON.stringify(lib));
  } catch (err) {
    console.warn('macros: persist failed', err);
  }
}

const [library, setLibrary] = createSignal<MacroLibrary>(load());

export const macroLibrary: Accessor<MacroLibrary> = library;
export const macros: Accessor<Macro[]> = () => library().macros;

/** Write a locally-authored library: stamp `modifiedAt` on what changed, so
 *  project sync can tell this copy is newer than one on another device. */
function writeLocal(next: MacroLibrary): void {
  const stamped = { ...next, macros: stampChanged(library().macros, next.macros, Date.now()) };
  setLibrary(stamped);
  persist(stamped);
}

/** Replace the entire library. Persists + emits a signal for subscribers. */
export function setMacroLibrary(next: MacroLibrary): void {
  macroHistory.checkpoint();
  writeLocal(next);
}

/** History handle for the macros library. Covers add/update/remove/reorder
 *  — everything through `setMacroLibrary`. Undo and redo stamp too - see
 *  `stampChanged`. */
export const macroHistory = createHistory<MacroLibrary>({
  name: 'macros',
  read: () => library(),
  write: (next) => writeLocal(next),
});

/** Adopt macros that project sync brought from another device, newer copy
 *  wins (`libraryMerge.ts`). NOT a local edit: no undo checkpoint and no
 *  re-stamp, so the sender's `modifiedAt` survives. Returns how many changed. */
export function applySyncedMacros(incoming: readonly Macro[]): number {
  const cur = library();
  const merged = mergeLibraryEntries(cur.macros, incoming);
  if (!merged) return 0;
  const before = new Map(cur.macros.map((m) => [m.id, m]));
  const next = { ...cur, macros: merged };
  setLibrary(next);
  persist(next);
  return merged.filter((m) => before.get(m.id) !== m).length;
}

/** Insert / replace a single macro by id. If a macro with this id exists,
 *  it's replaced; otherwise appended. */
export function upsertMacro(macro: Macro): void {
  const lib = library();
  const ix = lib.macros.findIndex((m) => m.id === macro.id);
  const nextMacros = ix === -1
    ? [...lib.macros, macro]
    : lib.macros.map((m, i) => (i === ix ? macro : m));
  setMacroLibrary({ ...lib, macros: nextMacros });
}

/** Remove a macro by id. */
export function removeMacro(macroId: string): void {
  const lib = library();
  setMacroLibrary({ ...lib, macros: lib.macros.filter((m) => m.id !== macroId) });
}

// ─── Editor open/close ─────────────────────────────────────────
// The MacrosEditor modal is opened from a few places (InspectorPanel on a
// MacroItem, future toolbar entries). Holding the signal here keeps the
// trigger sites de-coupled from App.tsx — anyone can call `openMacrosEditor()`
// and App.tsx renders the modal off the signal.

const [editorOpenSignal, setEditorOpenSignal] = createSignal(false);
export const macrosEditorOpen: Accessor<boolean> = editorOpenSignal;
export function openMacrosEditor(): void { setEditorOpenSignal(true); }
export function closeMacrosEditor(): void { setEditorOpenSignal(false); }
